import classnames from 'classnames';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import React, { ReactElement, useState } from 'react';
import EmptyCartIcon from '../../assets/logo/EmptyCartIcon';
import Theme from '../Theme';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { collect } from 'collect.js';
import { useTranslation } from 'react-i18next';
import BackIcon from '../../assets/logo/BackIcon';
import { CompleteBasketItem } from '../../providers/BasketProvider';

interface Props {
  amount: number;
  items: CompleteBasketItem[];
  extra?: ReactElement;
  className?: string;
}

export default function OrderBasketSummary({ amount, items, extra, className }: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Order:OrderBasketSummary' });

  const { formatCurrency } = useAppMethods();

  const [open, setOpen] = useState(false);

  function renderDescription(item: CompleteBasketItem) {
    return (
      item.modifiers.length > 0 && (
        <p className="mt-xs break-words text-gray-700 text-xs">
          {collect(item.modifiers)
            .filter((modifier) => !!modifier)
            .implode('entry.name', ' • ')}
        </p>
      )
    );
  }

  return (
    items.length > 0 && (
      <div className={classnames('bg-gray-550 border-b-gray px-lg py-sm', className)}>
        <Accordion
          style={{ boxShadow: 'none', backgroundColor: 'transparent' }}
          expanded={open}
          onChange={() => setOpen((current) => !current)}
        >
          <AccordionSummary
            style={{
              padding: 0,
              margin: 0,
              width: '100%',
              minHeight: 0,
            }}
            classes={{
              content: '!m-none',
            }}
          >
            <div className="w-full flex justify-between">
              <button className="my-auto flex text-blue-100 items-center">
                <EmptyCartIcon color={Theme.color.blue['100']} className="mr-xs" />
                <span>{t(open ? 'labels.hide' : 'labels.show_order_summary')}</span>
                <BackIcon
                  color={Theme.color.blue['100']}
                  className={classnames('!w-2 ml-xs transition delay-200', {
                    '-rotate-90': !open,
                    'rotate-90': open,
                  })}
                />
              </button>
              <h6 className="my-auto">{formatCurrency(amount)}</h6>
            </div>
          </AccordionSummary>
          <AccordionDetails style={{ padding: 0, marginTop: 0 }}>
            {items.map((item, index) => (
              <div className="mt-md flex justify-between" key={`basket-item-${index}`}>
                <div>
                  <p className="text-sm">
                    {item.quantity}x {item.entry.name}
                  </p>
                  <p className="text-gray-700 text-sm">{renderDescription(item)}</p>
                </div>
                <p className="text-sm">{formatCurrency(item.actual_price)}</p>
              </div>
            ))}
            {extra}
          </AccordionDetails>
        </Accordion>
      </div>
    )
  );
}
